import { useState, useEffect ,useMemo} from "react";
import { motion, useAnimation } from "framer-motion";

const ParallaxGallery = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const imageControls1 = useAnimation();
  const imageControls2 = useAnimation();
  const glitchControls = useAnimation();
  const textControls = useAnimation();
  

  // Image paths (assuming in public folder; replace with actual paths or imports)
  const images = [
    "https://studiomaamangala.com/wp-content/uploads/2025/05/camera-white.png", 
    "https://studiomaamangala.com/wp-content/uploads/2025/05/FUZIFILM.png", 
  ];

  // Random clip-path directions
  const clipDirections = useMemo(() => [
    { start: "inset(0 100% 0 0)", end: "inset(0 0 0 0)" }, // Left to right
    { start: "inset(0 0 0 100%)", end: "inset(0 0 0 0)" }, // Right to left
    { start: "inset(100% 0 0 0)", end: "inset(0 0 0 0)" }, // Top to bottom
    { start: "inset(0 0 100% 0)", end: "inset(0 0 0 0)" }, // Bottom to top
  ], []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Mouse/touch movement for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2; // Normalize -1 to 1
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };

    const handleTouchMove = (e) => {
      // e.preventDefault(); // Prevent scrolling
      const touch = e.touches[0];
      const x = (touch.clientX / window.innerWidth - 0.5) * 2;
      const y = (touch.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };

    if (isMobile) {
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      return () => window.removeEventListener("touchmove", handleTouchMove);
    } else {
      window.addEventListener("mousemove", handleMouseMove);
      return () => window.removeEventListener("mousemove", handleMouseMove);
    }
  }, [isMobile]);

  // Image transition and glitch
  useEffect(() => {
    const randomDirection = clipDirections[Math.floor(Math.random() * clipDirections.length)];

    // Animate current image
    imageControls1.start({
      clipPath: currentImage === 0 ? randomDirection.end : randomDirection.start,
      zIndex: currentImage === 0 ? 2 : 1,
      transition: { duration: 1, ease: "easeInOut" },
    });

    // Animate next image
    imageControls2.start({
      clipPath: currentImage === 1 ? randomDirection.end : randomDirection.start,
      zIndex: currentImage === 1 ? 2 : 1,
      transition: { duration: 1, ease: "easeInOut" },
    });

    // Glitch effect
    glitchControls.start({
      x: [0, 5, -5, 3, -3, 0],
      y: [0, 2, -2, 1, -1, 0],
      filter: [
        "hue-rotate(0deg) brightness(1)",
        "hue-rotate(10deg) brightness(1.2)",
        "hue-rotate(-10deg) brightness(0.8)",
        "hue-rotate(5deg) brightness(1.1)",
        "hue-rotate(0deg) brightness(1)",
      ],
      transition: { duration: 0.5, times: [0, 0.2, 0.4, 0.6, 0.8, 1] },
    });

    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev === 0 ? 1 : 0));
    }, 5000);

    return () => clearInterval(interval);
  }, [currentImage, imageControls1, imageControls2, glitchControls, clipDirections]);

  // Parallax effect for images and text
  useEffect(() => {
    // Image parallax
    imageControls1.start({
      rotateX: mousePos.y * 10,
      rotateY: -mousePos.x * 10,
      x: -mousePos.x * 20,
      y: -mousePos.y * 20,
      transition: { duration: 0.2 },
    });

    imageControls2.start({
      rotateX: mousePos.y * 5,
      rotateY: -mousePos.x * 5,
      x: -mousePos.x * 10,
      y: -mousePos.y * 10,
      transition: { duration: 0.2 },
    });

    // Subtle text parallax
    // textControls.start({
    //   x: -mousePos.x * 10,
    //   y: -mousePos.y * 10,
    //   transition: { duration: 0.3 },
    // });
  }, [mousePos, imageControls1, imageControls2, textControls]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "#000000",
        perspective: "1000px",
      }}
    >
      {/* Background Text */}
      <motion.div
        style={{
          position: "absolute",
          top: isMobile ? "5vw" : "10vw",
          left: "50%",
          transform: "translateX(-50%)",
          textAlign: "center",
          color: "#FFFFFF",
          opacity: 0.1,
          fontFamily: "'Montserrat', sans-serif",
          pointerEvents: "none",
        }}
        animate={textControls}
      >
        <div
          style={{
            fontSize: isMobile ? "10vw" : "8vw",
            fontWeight: "700",
            lineHeight: 1.2,
          }}
        >
          CAPTURE
        </div>
        <div
          style={{
            fontSize: isMobile ? "6vw" : "8vw",
            fontWeight: "400",
          }}
        >
          MOMENT
        </div>
      </motion.div>

      {/* Foreground Images */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "80vw" : "60vw",
          height: isMobile ? "60vw" : "40vw",
          perspective: "1000px",
        }}
      >
        {/* Image 1 */}
        <motion.div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
          }}
          animate={imageControls1}
        >
          <motion.img
            src={images[0]}
            alt="Image 1"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain", // Changed to contain for better PNG handling
            }}
            animate={glitchControls}
          />
        </motion.div>

        {/* Image 2 */}
        <motion.div
          style={{
            position: "absolute",
            display:"flex",
            justifyContent:"center",
            alignItems:"center",
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
          }}
          animate={imageControls2}
        >
          <motion.img
            src={images[1]}
            alt="Image 2"
            style={{
              width: "50%",
              height: "80%",
              objectFit: "contain",
            }}
            animate={glitchControls}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default ParallaxGallery;